import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { UTILS } from "utils";
import { logErrorOnSentry } from "utils/Utils";
import { useSsoLoginMutation } from "store/auth/register/registerService";
import {
  readComeraParams,
  persistComeraParams,
  stripTokenFromUrl,
  getComeraToken,
} from "./helpers";

/**
 * Signs in a customer arriving from the Comera Explore WebView.
 *
 * The token is a one-shot handoff credential: we trade it for our own session
 * and never use it again. Verification happens on the backend -- a signature
 * check the customer can bypass is not a signature check.
 *
 * When the uuid has not been linked to a user yet the backend answers
 * `needsLinking`, which is a normal first-visit branch and not an error. We take
 * no action: the customer browses as a guest and `ProtectedRoute` sends them to
 * the existing OTP screen when they reach the basket, at which point `verifyPin`
 * forwards the token and the backend binds the uuid.
 *
 * Returns `ssoPending` so `App` can hold its existing spinner and keep the route
 * guards from evaluating mid-exchange.
 */
function useComeraSso() {
  const { brandInfo } = useSelector((state) => state?.brandStorageReducer);
  const { error: brandError } = useSelector((state) => state?.brand);
  const brandId = brandInfo?.brand?._id;
  const [ssoLogin] = useSsoLoginMutation();
  const exchangedRef = useRef(false);
  // Resolve synchronously on the first render so a token in the URL holds the
  // spinner before any protected route can redirect.
  const [ssoPending, setSsoPending] = useState(
    () => Boolean(readComeraParams().token) || Boolean(getComeraToken())
  );

  // Capture the params once, as early as possible -- navigation would drop them.
  useEffect(() => {
    const { token, transactionId, paramName } = readComeraParams();

    if (token || transactionId) {
      persistComeraParams({ token, transactionId });
      stripTokenFromUrl();
    }
    if (paramName) {
      console.log(`comera-sso: token received via "${paramName}"`);
    }
    if (!getComeraToken()) {
      setSsoPending(false);
    }
  }, []);

  // Brand resolution failed, so there is nothing to exchange against -- release the
  // spinner and let App render its own error state.
  useEffect(() => {
    if (brandError) setSsoPending(false);
  }, [brandError]);

  useEffect(() => {
    const comeraToken = getComeraToken();

    if (!comeraToken) {
      setSsoPending(false);
      return;
    }
    if (exchangedRef.current) return;
    // The session is keyed by brand and the backend scopes the lookup by it.
    if (!brandId) return;

    // Already signed in on this device -- nothing to exchange.
    if (UTILS.getCurrentUser(brandId)?.token) {
      setSsoPending(false);
      return;
    }

    exchangedRef.current = true;

    const exchangeToken = async () => {
      try {
        const { data } = await ssoLogin({ brandId, comeraToken });

        if (data?.success === 1 && data?.data?.token) {
          // Mirrors the OTP flow so other tabs pick the session up.
          localStorage.setItem("loginEvent", Date.now().toString());
        } else if (!data?.data?.needsLinking) {
          // Expired or rejected token -- fall through to the OTP flow silently.
          logErrorOnSentry(
            "comera-sso: token exchange did not return a session",
            "warning",
            JSON.stringify(data?.message ?? data)
          );
        }
      } catch (error) {
        logErrorOnSentry(
          `comera-sso: token exchange failed ${error?.message}`,
          "error",
          JSON.stringify(error)
        );
      } finally {
        setSsoPending(false);
      }
    };

    exchangeToken();
  }, [brandId, ssoLogin]);

  return { ssoPending };
}

export default useComeraSso;
